import { z } from 'zod';

/**
 * Shared content-block turn contract for the conversation store — the
 * Galapagos expression of the turn shape shared with Pangea (Convergence
 * Option 2). Field names are snake_case on purpose: the blocks are persisted
 * verbatim into `messages.content` jsonb, and the stored bytes are the
 * cross-product contract (a jsonb blob keeps its stored keys — ADR-066), so
 * the wire shape matches the Python side rather than this repo's camelCase
 * API convention.
 *
 * This is a byte-for-byte mirror of Pangea's `shared/schemas/conversation/turn.json`
 * (the `EnrichedTurn` producer): the merge that unions the two stores must be
 * a data union, not a schema migration, so the shape is frozen against a
 * vendored copy of that JSON Schema (see `pangea-turn.schema.json` and its
 * conformance test). Blocks are produced by our own transcript processor (not
 * a third party), so the schemas are closed — an unknown block shape is a
 * processor bug we want surfaced, not tolerated.
 */

export const textBlockSchema = z
  .object({
    type: z.literal('text'),
    text: z.string().min(1),
  })
  .strict();
export type TextBlock = z.infer<typeof textBlockSchema>;

export const toolUseBlockSchema = z
  .object({
    type: z.literal('tool_use'),
    /** Correlates a tool_result block back to its call (provider `request_id`). */
    id: z.string(),
    name: z.string(),
    input: z.record(z.string(), z.unknown()),
  })
  .strict();
export type ToolUseBlock = z.infer<typeof toolUseBlockSchema>;

export const toolResultBlockSchema = z
  .object({
    type: z.literal('tool_result'),
    tool_use_id: z.string(),
    /** Stringified tool output — Pangea's `turn.json` types this as a string. */
    content: z.string(),
    is_error: z.boolean().default(false),
  })
  .strict();
export type ToolResultBlock = z.infer<typeof toolResultBlockSchema>;

export const contentBlockSchema = z.discriminatedUnion('type', [
  textBlockSchema,
  toolUseBlockSchema,
  toolResultBlockSchema,
]);
export type ContentBlock = z.infer<typeof contentBlockSchema>;

// Frozen Pangea wire vocabularies. These do not back the internal messages
// table or native authoring contracts. `role` is `user | agent` only — any
// unsupported source role is dropped by the egress adapter, never relabelled.
export const CONVERSATION_ROLE = ['user', 'agent'] as const;
export const conversationRoleSchema = z.enum(CONVERSATION_ROLE);
export type ConversationRole = z.infer<typeof conversationRoleSchema>;

export const CONVERSATION_STATUS = ['processing', 'completed', 'failed'] as const;
export const conversationStatusSchema = z.enum(CONVERSATION_STATUS);
export type ConversationStatus = z.infer<typeof conversationStatusSchema>;

/**
 * One enriched conversation turn: a role plus its ordered content blocks, an
 * absolute Unix `timestamp` (call start + the turn's in-call offset), and the
 * per-turn provider metadata Pangea carries (`interrupted` on agent turns,
 * `source_medium` on user turns). This is an egress/read-projection shape, not
 * a second stored transcript. A turn always has at least one content block —
 * empty-content turns are dropped before adapter processing.
 */
export const enrichedTurnSchema = z.object({
  role: conversationRoleSchema,
  content: z.array(contentBlockSchema).min(1),
  timestamp: z.number().int().nonnegative(),
  interrupted: z.boolean().nullable().default(null),
  source_medium: z.string().nullable().default(null),
});
export type EnrichedTurn = z.infer<typeof enrichedTurnSchema>;

/**
 * An enriched turn as the staff transcript read projects it: the same turn plus
 * its stored `messages.position`. A read-side shape only — deliberately NOT a
 * change to `enrichedTurnSchema`, which doubles as the Pangea ingestion `Turn`
 * contract and must keep its frozen key set.
 */
export const positionedEnrichedTurnSchema = enrichedTurnSchema.extend({
  position: z.number().int().nonnegative(),
});
export type PositionedEnrichedTurn = z.infer<typeof positionedEnrichedTurnSchema>;

/**
 * The `IngestedConversation` wire contract — the normalized document Pangea's
 * cross-account ingestion consumes. A Zod expression of Pangea's vendored
 * `pangea-ingestion.schema.json` (see its conformance test).
 *
 * The top-level object plus `context` and `source_meta` are `.strict()` where
 * the contract is `additionalProperties:false`; `tool_calls` entries are
 * `.passthrough()` because the contract's `GenericToolCall` is
 * `additionalProperties:true`. `transcript` reuses `enrichedTurnSchema` — the
 * same content-block shape already frozen against Pangea's `turn.json`.
 * `enrichedTurnSchema` is non-strict Zod, which STRIPS unknown keys on `.parse()`
 * rather than rejecting them — so an extraneous turn-level field (e.g. an
 * ElevenLabs-only `time_in_call_secs`) is dropped, and the emitted document
 * stays valid against the vendored `additionalProperties:false` `Turn` (proven
 * by the builder's strip test). The builder validates the whole document
 * through this schema before returning.
 */

/** `schema_version` — the frozen contract version this producer emits. */
export const INGESTION_SCHEMA_VERSION = 'ingestion.conversation.v1' as const;
/** `source_system` — the ingestion folder/prefix key Galapagos ships under. */
export const INGESTION_SOURCE_SYSTEM = 'galapagos' as const;
/**
 * `user_id` for an unresolved member binding. The contract requires a non-null
 * string, so an unattributed conversation carries the empty-string sentinel
 * (the contract's `user_id` is an unconstrained string) rather than null.
 */
export const ANON_USER_ID = '' as const;

export const ingestionContextSchema = z
  .object({
    agent_id: z.string(),
    agent_type: z.string(),
    conversation_id: z.string(),
    user_id: z.string(),
    language: z.string().nullable().optional(),
    duration_sec: z.number().int().nonnegative(),
  })
  .strict();
export type IngestionContext = z.infer<typeof ingestionContextSchema>;

export const genericToolCallSchema = z
  .object({
    tool_name: z.string(),
    params: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();
export type GenericToolCall = z.infer<typeof genericToolCallSchema>;

export const ingestionSourceMetaSchema = z
  .object({
    schema_version: z.literal(INGESTION_SCHEMA_VERSION),
    source_system: z.literal(INGESTION_SOURCE_SYSTEM),
    conversation_type: z.string(),
    /** ElevenLabs conversation id — Pangea's key to pull the call audio. */
    voice_provider_conversation_id: z.string(),
    /** Idempotency key with `conversation_id`; corrected re-sends increment. */
    revision: z.number().int().nonnegative(),
    /**
     * Template-level prompt disclosure. Deliberately a bare object HERE: this
     * schema mirrors `pangea-ingestion.schema.json`, a byte-for-byte vendored
     * copy of Pangea's contract, and the conformance test holds the two in
     * step. Tightening this to our own `promptParamsSchema` (see
     * ./prompt-params.ts) would break that guard and turn the integration into
     * a private fork — the strict validation belongs on the producer side, and
     * lives there.
     */
    prompt_params: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .strict();
export type IngestionSourceMeta = z.infer<typeof ingestionSourceMetaSchema>;

export const ingestedConversationSchema = z
  .object({
    context: ingestionContextSchema,
    transcript: z.array(enrichedTurnSchema),
    tool_calls: z.array(genericToolCallSchema).optional(),
    summary: z.string().nullable().optional(),
    source_meta: ingestionSourceMetaSchema,
  })
  .strict();
export type IngestedConversation = z.infer<typeof ingestedConversationSchema>;
